import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, RotateCcw, Clock, CheckCircle, Lock, AlertTriangle } from 'lucide-react';
import apiService from '../../services/api';
import { useTutorialStore } from '../../store/tutorialStore';
import { useProjectStore } from '../../store';
import { TutorialStatus } from '../../types/tutorial';
import {
  TUTORIAL_CATALOG,
  TutorialCatalogEntry,
  TUTORIAL_SECTIONS,
  TUTORIAL_SECTION_MAP,
  TUTORIAL_WORKFLOW_NAMES,
  TUTORIAL_WORKFLOW_RESTART_STEP,
  getTutorialStep,
} from '../../config/tutorialConfig';

const DIFFICULTY_BADGES: Record<string, { label: string; className: string }> = {
  beginner: { label: 'Beginner', className: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  intermediate: { label: 'Intermediate', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
  advanced: { label: 'Advanced', className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
};

const BootcampTutorials: React.FC = () => {
  const navigate = useNavigate();

  const {
    status: tutorialStatus,
    currentStep,
    totalSteps,
    activeTutorialId,
    isOverlayHidden,
    startTutorialById,
    restartTutorialById,
    resumeTutorialById,
  } = useTutorialStore();

  const [tutorialStatuses, setTutorialStatuses] = useState<Record<string, { status: TutorialStatus; currentStep: number; totalSteps: number }>>({});
  /** Map of section project name -> project ID (null = not yet created) */
  const [sectionProjectIds, setSectionProjectIds] = useState<Record<string, string | null>>({});
  /** Tutorial pending restart confirmation (null = dialog closed) */
  const [restartConfirmTutorial, setRestartConfirmTutorial] = useState<TutorialCatalogEntry | null>(null);
  const [isRestarting, setIsRestarting] = useState(false);

  useEffect(() => {
    const loadStatuses = async () => {
      const statuses: Record<string, { status: TutorialStatus; currentStep: number; totalSteps: number }> = {};
      for (const tutorial of TUTORIAL_CATALOG) {
        try {
          const response = await apiService.getTutorialProgress(tutorial.id);
          statuses[tutorial.id] = {
            status: response.status,
            currentStep: response.current_step ?? 0,
            totalSteps: tutorial.stepCount,
          };
        } catch {
          statuses[tutorial.id] = {
            status: 'not_started',
            currentStep: 0,
            totalSteps: tutorial.stepCount,
          };
        }
      }
      setTutorialStatuses(statuses);
    };
    loadStatuses();

    // Discover project IDs for each section's project name
    const discoverSectionProjects = async () => {
      const { projects, loadProjects } = useProjectStore.getState();
      let allProjects = projects;
      if (allProjects.length === 0) {
        await loadProjects();
        allProjects = useProjectStore.getState().projects;
      }
      const ids: Record<string, string | null> = {};
      for (const section of TUTORIAL_SECTIONS) {
        const match = allProjects.find(p => p.name === section.projectName);
        ids[section.projectName] = match?.project_id ?? null;
      }
      setSectionProjectIds(ids);
    };
    discoverSectionProjects();
  }, []);

  /** Get the project ID for a tutorial's section, auto-creating if needed. */
  const getOrCreateSectionProject = async (tutorial: TutorialCatalogEntry): Promise<string | null> => {
    const section = TUTORIAL_SECTION_MAP[tutorial.section];
    if (!section) return null;

    const existing = sectionProjectIds[section.projectName];
    if (existing) return existing;

    // Auto-create the section's project
    try {
      const { createProject, loadProjects } = useProjectStore.getState();
      const result = await createProject({
        name: section.projectName,
        description: section.projectDescription,
      });
      if (result.success) {
        await loadProjects();
        const allProjects = useProjectStore.getState().projects;
        const created = allProjects.find(p => p.name === section.projectName);
        const newId = created?.project_id ?? null;
        setSectionProjectIds(prev => ({ ...prev, [section.projectName]: newId }));
        return newId;
      }
    } catch { /* fall through */ }
    return null;
  };

  /** Resolve the live project ID for a tutorial's section. */
  const getProjectIdForTutorial = (tutorial: TutorialCatalogEntry): string | null => {
    const section = TUTORIAL_SECTION_MAP[tutorial.section];
    if (!section) return null;
    return sectionProjectIds[section.projectName] ?? null;
  };

  const handleTutorialAction = async (tutorial: TutorialCatalogEntry) => {
    const status = tutorialStatuses[tutorial.id]?.status || 'not_started';

    if (activeTutorialId === tutorial.id && tutorialStatus === 'in_progress' && !isOverlayHidden) {
      return;
    }

    if (status === 'completed' || status === 'dismissed') {
      await restartTutorialById(tutorial.id);
    } else if (status === 'in_progress') {
      // Resume preserves stored step instead of resetting to 1.
      const progress = await resumeTutorialById(tutorial.id);

      if (progress) {
        const resumedStep = progress.current_step ?? 1;
        const ctx = progress.context_data as Record<string, unknown> | undefined;

        // Walk backward from the resumed step to find one we can navigate to.
        let targetStep = resumedStep;
        let stepConfig = getTutorialStep(targetStep, tutorial.id);

        while (targetStep > 1 && stepConfig) {
          if (stepConfig.resumeFallbackStep) {
            targetStep = stepConfig.resumeFallbackStep;
            stepConfig = getTutorialStep(targetStep, tutorial.id);
            continue;
          }
          if (stepConfig.expectedRoute || stepConfig.targetSelector.startsWith('[data-tutorial="nav-')) {
            break;
          }
          if (stepConfig.targetSelector.includes('btn-create-')) {
            break;
          }
          targetStep--;
          stepConfig = getTutorialStep(targetStep, tutorial.id);
        }

        // Update the step if we walked backward
        if (targetStep !== resumedStep) {
          useTutorialStore.setState({ currentStep: targetStep });
          apiService.updateTutorialProgress({ current_step: targetStep }, tutorial.id).catch(() => {});
        }

        // Resolve the actual project ID — prefer the live section project
        const sectionProjectId = getProjectIdForTutorial(tutorial);
        const liveOperationId = sectionProjectId ?? (ctx?.operation_id as string | undefined);

        // Navigate based on the resolved step
        if (stepConfig?.expectedRoute) {
          const route = stepConfig.expectedRoute;
          if (route.includes(':id')) {
            if (route.includes('workflows')) {
              // Always resolve workflow by name
              if (liveOperationId) {
                try {
                  const workflows = await apiService.getWorkflows(String(liveOperationId));
                  const expectedName = TUTORIAL_WORKFLOW_NAMES[tutorial.id];
                  const match = expectedName
                    ? workflows.find(w => w.name === expectedName)
                    : undefined;
                  if (match) {
                    useTutorialStore.getState().updateTutorialContext({ workflow_id: match.orchestration_id });
                    navigate(`/projects/${liveOperationId}/workflows/${match.orchestration_id}`);
                    return;
                  }
                } catch { /* ignore lookup failure */ }
              }
              // Workflow not found — reset to workflow creation step
              if (liveOperationId) {
                const restartStep = TUTORIAL_WORKFLOW_RESTART_STEP[tutorial.id] ?? 2;
                useTutorialStore.setState({ currentStep: restartStep });
                apiService.updateTutorialProgress({ current_step: restartStep }, tutorial.id).catch(() => {});
                navigate(`/projects/${liveOperationId}/workflows`);
                return;
              }
            }
            if (route.includes('projects') && liveOperationId) {
              navigate(route.replace(':id', String(liveOperationId)));
              return;
            }
            if (route.includes('projects')) {
              useTutorialStore.setState({ currentStep: 2 });
              apiService.updateTutorialProgress({ current_step: 2 }, tutorial.id).catch(() => {});
              navigate('/projects');
              return;
            }
          }
          // Handle workflows list route
          if (route === '/workflows' && liveOperationId) {
            navigate(`/projects/${liveOperationId}/workflows`);
            return;
          }
          navigate(route);
          return;
        }

        // Fallback: resolve workflow by name
        if (liveOperationId) {
          try {
            const workflows = await apiService.getWorkflows(String(liveOperationId));
            const expectedName = TUTORIAL_WORKFLOW_NAMES[tutorial.id];
            const match = expectedName
              ? workflows.find(w => w.name === expectedName)
              : undefined;
            if (match) {
              useTutorialStore.getState().updateTutorialContext({ workflow_id: match.orchestration_id });
              navigate(`/projects/${liveOperationId}/workflows/${match.orchestration_id}`);
              return;
            }
          } catch { /* ignore */ }
          // Workflow not found — reset to workflow creation step
          const restartStep = TUTORIAL_WORKFLOW_RESTART_STEP[tutorial.id] ?? 2;
          useTutorialStore.setState({ currentStep: restartStep });
          apiService.updateTutorialProgress({ current_step: restartStep }, tutorial.id).catch(() => {});
          navigate(`/projects/${liveOperationId}/workflows`);
          return;
        }

        // Last resort: navigate based on target selector
        if (stepConfig?.targetSelector.includes('nav-projects') || stepConfig?.targetSelector.includes('btn-create-project')) {
          navigate('/projects');
          return;
        }
        if (stepConfig?.targetSelector.includes('nav-workflows') || stepConfig?.targetSelector.includes('btn-create-workflow')) {
          navigate('/projects');
          return;
        }
      }
    } else {
      await startTutorialById(tutorial.id);
    }

    // Navigate to the section's project for tutorials that require it
    if (tutorial.requiresOperation) {
      const projectId = getProjectIdForTutorial(tutorial);
      if (projectId) {
        navigate(`/projects/${projectId}`);
      }
    }

    // For tutorials whose section auto-creates a project (e.g. email_mcp),
    // create the project and navigate into it
    if (!tutorial.requiresOperation && tutorial.section) {
      const section = TUTORIAL_SECTION_MAP[tutorial.section];
      if (section) {
        const projectId = await getOrCreateSectionProject(tutorial);
        if (projectId) {
          navigate(`/projects/${projectId}`);
        }
      }
    }
  };

  const handleRestartAction = (tutorial: TutorialCatalogEntry) => {
    setRestartConfirmTutorial(tutorial);
  };

  const confirmRestart = async () => {
    const tutorial = restartConfirmTutorial;
    if (!tutorial || isRestarting) return;
    setIsRestarting(true);
    try {
      await restartTutorialById(tutorial.id);

      if (tutorial.requiresOperation) {
        const projectId = getProjectIdForTutorial(tutorial);
        if (projectId) {
          navigate(`/projects/${projectId}`);
        }
      }
      if (!tutorial.requiresOperation && tutorial.section) {
        const section = TUTORIAL_SECTION_MAP[tutorial.section];
        if (section) {
          const projectId = await getOrCreateSectionProject(tutorial);
          if (projectId) {
            navigate(`/projects/${projectId}`);
          }
        }
      }
    } finally {
      setIsRestarting(false);
      setRestartConfirmTutorial(null);
    }
  };

  const getTutorialStatusInfo = (tutorial: TutorialCatalogEntry) => {
    // Tutorials that require an existing project are locked until it exists
    if (tutorial.requiresOperation) {
      const projectId = getProjectIdForTutorial(tutorial);
      if (projectId === null) {
        return { label: 'Requires "Build a Movie Summarizer"', buttonLabel: 'Locked', disabled: true, showRestart: false };
      }
    }

    // Tutorials that require another tutorial to be completed first
    if (tutorial.requiresTutorial) {
      const prereqStatus = tutorialStatuses[tutorial.requiresTutorial]?.status;
      if (prereqStatus !== 'completed') {
        const prereqEntry = TUTORIAL_CATALOG.find(t => t.id === tutorial.requiresTutorial);
        const prereqName = prereqEntry?.title || tutorial.requiresTutorial;
        return { label: `Requires "${prereqName}"`, buttonLabel: 'Locked', disabled: true, showRestart: false };
      }
    }

    const isActiveTutorial = activeTutorialId === tutorial.id && tutorialStatus === 'in_progress';
    const savedStatus = tutorialStatuses[tutorial.id];
    const status = isActiveTutorial ? tutorialStatus : (savedStatus?.status || 'not_started');
    const step = isActiveTutorial ? currentStep : (savedStatus?.currentStep || 0);
    const steps = isActiveTutorial ? totalSteps : (savedStatus?.totalSteps || tutorial.stepCount);

    switch (status) {
      case 'in_progress':
        return { label: `Step ${step} of ${steps}`, buttonLabel: 'Resume', disabled: false, showRestart: true };
      case 'completed':
        return { label: 'Completed', buttonLabel: 'Restart', disabled: false, showRestart: false };
      case 'dismissed':
        return { label: 'Skipped', buttonLabel: 'Start Again', disabled: false, showRestart: false };
      default:
        return { label: 'Not Started', buttonLabel: 'Start Tutorial', disabled: false, showRestart: false };
    }
  };

  // Group tutorials by section
  const tutorialsBySection = TUTORIAL_SECTIONS.map(section => ({
    section,
    tutorials: TUTORIAL_CATALOG.filter(t => t.section === section.id),
  })).filter(group => group.tutorials.length > 0);

  return (
    <div className="p-4 sm:p-6 overflow-auto h-full">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/bootcamp')}
          className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-warm-white transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Bootcamp
        </button>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-warm-white mb-2">
            Tutorials
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Step-by-step interactive guides to help you master Agent Commander.
          </p>
        </div>

        {/* Tutorial Sections */}
        <div className="space-y-8">
          {tutorialsBySection.map(({ section, tutorials }) => (
            <div key={section.id} className="bg-white dark:bg-cap-dark p-6 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">
                  {section.title}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {section.description}
                </p>
              </div>
              <div className="space-y-4">
                {tutorials.map((tutorial) => {
                  const diffBadge = DIFFICULTY_BADGES[tutorial.difficulty];
                  const statusInfo = getTutorialStatusInfo(tutorial);
                  return (
                    <div
                      key={tutorial.id}
                      className="flex items-start gap-4 p-4 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-base font-semibold text-gray-900 dark:text-warm-white">
                            {tutorial.title}
                          </h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${diffBadge.className}`}>
                            {diffBadge.label}
                          </span>
                          {tutorialStatuses[tutorial.id]?.status === 'completed' && (
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                          {tutorial.description}
                        </p>
                        {tutorial.prerequisite && (
                          (tutorial.requiresOperation && getProjectIdForTutorial(tutorial) === null) ||
                          (tutorial.requiresTutorial && tutorialStatuses[tutorial.requiresTutorial]?.status !== 'completed')
                        ) && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mb-1">
                            {tutorial.prerequisite}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {tutorial.estimatedMinutes} min
                          </span>
                          <span>{tutorial.stepCount} steps</span>
                          <span>{statusInfo.label}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleTutorialAction(tutorial)}
                          disabled={statusInfo.disabled}
                          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                            statusInfo.disabled
                              ? 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500 cursor-not-allowed'
                              : 'text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                          }`}
                        >
                          {statusInfo.disabled ? (
                            <Lock className="w-4 h-4" />
                          ) : tutorialStatuses[tutorial.id]?.status === 'completed' || tutorialStatuses[tutorial.id]?.status === 'dismissed' ? (
                            <RotateCcw className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                          {statusInfo.buttonLabel}
                        </button>
                        {statusInfo.showRestart && (
                          <button
                            onClick={() => handleRestartAction(tutorial)}
                            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-gray-200 dark:border-gray-600"
                            title="Restart tutorial from the beginning"
                          >
                            <RotateCcw className="w-4 h-4" />
                            Restart
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Restart Confirmation Dialog */}
      {restartConfirmTutorial && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="restart-confirm-title"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3
                id="restart-confirm-title"
                className="text-lg font-semibold text-gray-900 dark:text-white"
              >
                Restart Tutorial?
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              This will restart <span className="font-medium text-gray-900 dark:text-gray-200">{restartConfirmTutorial.title}</span> from the beginning. Your current progress and any workflows, agents, and schemas created during the tutorial will be deleted.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRestartConfirmTutorial(null)}
                disabled={isRestarting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRestart}
                disabled={isRestarting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isRestarting ? 'Restarting...' : 'Restart'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BootcampTutorials;
